import React from 'react';
import {
  SUBSCRIPTIONS_UPDATED,
  WebhookSubscription,
  useWebhookSubscriptionsService,
  WebhookSubscriptionsFilter
} from 'nekst-api';
import {
  Cell, DateTimeField,
  FilterContext,
  List,
  ListDataRefresher,
  SimpleBlock,
  useShowContextRequired
} from 'ui-builder';
import InnerBlock from '../../../shared/web/layout/InnerBlock';
import { CreateWebhookSubscriptionButton } from './CreateWebhookSubscriptionForm';
import { GridLayout, IconDeleteButton } from 'features/nekst-widgets';
import TextField from '../../../shared/uibuilder/show/field/TextField';
import { useSubscriptionEventHelper } from './subscriptionEventHelper';
import WebhookRequestLogEntriesButton from './monitoring/WebhookRequestLogEntriesButton';
import SubscriptionTypeFilter from './SubscriptionTypeFilter';

import styles from './WebhookSubscriptionsList.module.scss';
import OnlyManuallyCreatedFilter from './OnlyManuallyCreatedFilter';

function DeleteWebhookSubscriptionButton() {
  const showContext = useShowContextRequired<WebhookSubscription>();
  const webhookSubscriptionsService = useWebhookSubscriptionsService();

  const subscription = showContext.data;

  const isDisabled = subscription.isCreatedViaApi;

  const getTitle = () => {
    if (isDisabled) {
      if (subscription.webhookUrl.includes('zapier.')) {
        return 'This webhook is managed via Zapier. Visit Zapier Dashboard to edit'
      } else {
        return 'This webhook was created via API and cannot be managed from this portal'
      }
    } else {
      return undefined;
    }
  }

  return (
    <IconDeleteButton
      deleteFunc={async () => {
        await webhookSubscriptionsService.cancelSubscription(showContext.data!.id);
      }}
      message={`Are you sure you want to delete this webhook subscription?`}
      disabled={isDisabled}
      title={getTitle()}
    />
  );
}

function EventTypeField() {
  const showContext = useShowContextRequired<WebhookSubscription>();

  const subscriptionEventHelper = useSubscriptionEventHelper();

  return (
    <>{subscriptionEventHelper.getLabel(showContext.data!.eventType)}</>
  );
}

export default function WebhookSubscriptionsList() {
  const webhookSubscriptionsService = useWebhookSubscriptionsService();

  return (
    <FilterContext<WebhookSubscriptionsFilter> initialValue={{
      onlyManuallyCreated: true,
    }}>
      <InnerBlock title="Webhook Subscriptions" headerRight={<CreateWebhookSubscriptionButton />}>
        <List
          getDataFunc={webhookSubscriptionsService.getSubscriptions}
          loadDataOnFilterUpdate
        >
          <SimpleBlock className={`${styles.filter} form`}>
            <SubscriptionTypeFilter />
            <OnlyManuallyCreatedFilter />
          </SimpleBlock>
          <GridLayout
            weights={[4, 3, 2, 2, 1]}
            noDataMessage="No webhook subscriptions configured."
          >
            <TextField source="webhookUrl" label="Webhook URL" />
            <Cell label="Event Type">
              <EventTypeField />
            </Cell>
            <Cell label="Status">
              {(item: WebhookSubscription) => (
                item.isEnabled ? 'Enabled' : 'Disabled'
              )}
            </Cell>
            <Cell label="Created At">
              <DateTimeField source="createdAt" label="Created At" />
            </Cell>
            <Cell label="Actions">
              <WebhookRequestLogEntriesButton />
              <DeleteWebhookSubscriptionButton />
            </Cell>
          </GridLayout>
          <ListDataRefresher eventName={SUBSCRIPTIONS_UPDATED} />
        </List>
      </InnerBlock>
    </FilterContext>
  );
}
